import { Feature } from "geojson";
import { Polygon } from "leaflet";

export enum FormWizardSteps {
  Home = "home",
  LandHolderConfirmation = "landholder-confirmation",
  LandHolderDetails = "landholder-details",
  NotifierDetails = "notifier-details",
  LotOnPlan = "lot-on-plan",
  SelectPurpose = "select-purpose",
  SelectCategory = "select-category",
  SelfAudit = "self-audit",
  ExchangeArea = "exchange-area",
  SelectActivity = "select-activities",
  SelectRegionalEcosystemStatus = "select-regional-ecosystem-status",
  SelectRegionalEcosystem = "select-regional-ecosystem",
  Reason = "reason",
  ChannelDimensions = "channel-dimensions",
  NotificationArea = "notification-area",
  Acknowledgements = "acknowledgements",
  ReviewNotification = "review-notification",
  Declarations = "declarations",
  AcknowledgeNotification = "acknowledge-notification"
}

export enum ActivityCodeEnum {
  airstripOrHelipads = 'AirstripOrHelipads',
  contaminantRemoval = 'ContaminantRemoval',
  channelDiversion = 'ChannelDiversion',
  boundaryFences = 'BoundaryFences',
  internalFences = 'InternalFences',
  tracks = 'Tracks',
  contourBanks = 'ContourBanks',
  irrigationChannels = 'IrrigationChannels',
  otherLinearInfrastructure = 'OtherLinearInfrastructure',
  otherNonLinearInfrastructure = 'OtherNonLinearInfrastructure',
  fireManagementLines = 'FireManagementLines',
  removeContaminant = 'RemoveContaminant',
  restoration = 'Restoration',
  other = 'Other',
  firebreaks = 'Firebreaks',
  drainageAndErosionManagement = 'DrainageAndErosionManagement',
  floodPreparation = 'FloodPreparation',
  smallScaleHarvesting = 'SmallScaleHarvesting',
  specialityTimberHarvesting = 'SpecialityTimberHarvesting',
  sourcingTimberInfrastructure = 'SourcingTimberInfrastructure',
  selectiveHarvestingSilviculturalInCypressREs = 'SelectiveHarvestingSilviculturalInCypressREs',
  silviculturalTreatmentHardwoodREs = 'SilviculturalTreatmentHardwoodREs',
  selectiveHarvestingInDenseREs = 'SelectiveHarvestingInDenseREs',
  selectiveHarvestingInHardwoodREs = 'SelectiveHarvestingInHardwoodDomiREs',
  groupSelectionHarvesting = 'GroupSelectionHarvesting',
}

export enum CategoryEnum {
  catA = 'A',
  catB = 'B',
  catC = 'C',
  catR = 'R',
  catX = 'X',
}

export enum CategoryLabelEnum {
  catA = 'Category A area',
  catB = 'Category B area',
  catC = 'Category C area',
  catR = 'Category R area',
  catX = 'Category X area',
}

export enum BiotaLayerNameEnum {
  essentialHabitat = 'Essential habitat',
  waterCourse = 'Watercourse or drainage feature',
  wetLand = 'Wetland'
}

export enum RegionalEcosystemStatusCodeEnum {
  endangered = 'Endangered',
  essentialHabitat = 'Essential habitat',
  leastConcern = 'Least Concern',
  ofConcern = 'Of Concern',
  koalaHabitat = 'Koala habitat',
}

export enum NotificationAreaLodgementTypeEnum {
  manualUpload = 'map',
  spatialDataCapture = 'gps',
}

export enum PurposeEnum {
  infrastructure = 'Infrastructure',
  agriculturalEfficiency = 'AgriculturalEfficiency',
  agricultureAndGrazing = 'AgricultureAndGrazing',
  encroachment = 'Encroachment',
  extractiveIndustry = 'Extractive Industry',
  fodder = 'Fodder',
  generalPurposes = 'General Purposes',
  nativeForestPractice = 'NativeForestPractice',
  certifiedNativeForestPractice = 'CertifiedNativeForestPractice',
  necessaryPropertyInfrastructure = 'Necessary property infrastructure',
  necessaryEnvironmentalWorks = 'Necessary Environmental Works',
  necessaryEnvironmentalClearing = 'NecessaryEnvironmentalClearing',
  publicSafety = 'Public Safety',
  thinning = 'Thinning',
  weedControl = 'Weed Control',
  coordinatedProjects = 'CoordinatedProjects',
  agriculture = 'Agriculture',
  managingRegrowthDensity = 'ManagingRegrowthDensity',
  managingWeeds = 'ManagingWeeds',
}

export enum ReferencesEnum {
  queenslandState = 'Queensland',
  australiaCountry = 'Australia',
  queenslandStateId = 6,
  australiaCountryId = 13
}

export enum QuestionReasonEnum {
  sawLogs = 'Saw logs',
  polesAndPiles = 'Poles and piles',
  salvageLogs = 'Salvage logs',
  fencePosts = 'Fence posts',
  woodChipsForTheExportMarket = 'Wood chips for the export market',
  silviculturalTreatment = 'Silvicultural treatment',
  other = 'Other'
}

// see https://itp-qld.atlassian.net/browse/OSACN-175
// https://www.rapidtables.com/convert/color/rgb-to-hex.html
export enum CategoryColourDisplayEnum {
  catA = '#FF00A8',
  catB = '#005CE6',
  catC = '#73DFFF',
  catR = '#FFFF00',
  catX = '#FFFFFF',
}

export enum BiotaColourDisplayEnum {
  essentialHabitat = '#004DA8',
  waterCourse = '#0000FF',
  wetLand = '#66935C'
}

export enum ReVMStatusCodesEnum {
  reCatAorBAreaEndangered = 'rem_end',
  reCatAorBAreaContainConcern = 'rem_oc',
  reCatAorBAreaLeastConcern = 'rem_leastc',
  reCatCorRAreaEndangered = 'hvr_end',
  reCatCorRAreaContainConcern = 'hvr_oc',
  reCatCorRAreaLeastConcern = 'hvr_leastc',
  reRegrowthVegetation = 'hvr_regrowth',
  reRemnant = 'rem_remnant',
  reNonRemnant = 'non-rem',
  reWater = 'water',
  categoryX = 'categoryx',
  plantation = 'plantation',
}

export interface IReGroupByVmStatusIdAndFeatures {
  reVmStatId: string;
  features: any;
}

export interface IRegionalEcosystemInfoAndColour {
  reRuleIdDescription: string;
  colourDisplay: string;
  reVmStatusString: string;
  reVmPoly: string;
  reVmStatLookupIds: number[];
}

export enum RegionalEcosystemColourEnum {
  // see jira https://itp-qld.atlassian.net/browse/OSACN-175
  // https://www.rapidtables.com/convert/color/rgb-to-hex.html
  reCatAorBAreaEndangered = '#FF36FF',
  reCatAorBAreaContainConcern = '#FF660F',
  reCatAorBAreaLeastConcern = '#7DFFB1',
  reCatCorRAreaEndangered = '#FFA8FF',
  reCatCorRAreaContainConcern = '#FFD091',
  reCatCorRAreaLeastConcern = '#D7FFB1',
  reRegrowthVegetation = '#D9FBC8',
  reRemnant = '#BBDDAA',
  reNonRemnant = '#FFFFFF',
  reWater = '#ADD8E6',
}

export enum RegionalEcosystemDescEnum {
  // see jira https://itp-qld.atlassian.net/browse/OSACN-52
  reCatAorBAreaEndangered = 'Category A or B containing endangered',
  reCatAorBAreaContainConcern = 'Category A or B containing of concern',
  reCatAorBAreaLeastConcern = 'Category A or B that is of least concern',
  reCatCorRAreaEndangered = 'Category C or R containing endangered',
  reCatCorRAreaContainConcern = 'Category C or R containing of concern',
  reCatCorRAreaLeastConcern = 'Category C or R that is of least concern',
  reRegrowthVegetation = 'RegrowthVegetation',
  reRemnant = 'Remnant Vegetation',
  reNonRemnant = 'non-remnant',
  reWater = 'Water',
}

export enum vmStatIdLookup {
  reCatAorBAreaEndangered_1 = 1,
  reCatAorBAreaEndangered_2 = 2,
  reCatAorBAreaContainConcern_3 = 3,
  reCatAorBAreaContainConcern_4 = 4,
  reCatAorBAreaLeastConcern = 5,
  reCatCorRAreaEndangered_11 = 11,
  reCatCorRAreaEndangered_12 = 12,
  reCatCorRAreaContainConcern_13 = 13,
  reCatCorRAreaContainConcern_14 = 14,
  reCatCorRAreaLeastConcern = 15,
  reWater_9 = 9,
  reWater_19 = 19,
  reUnknown = -1,
}

// Data and reference names are from ESRI spatial service layer 9
// https://gisservices2/arcgis/rest/services/EnterpriseIntegration/LotPlanSearch/MapServer/9
export const regionalEcosystemInfoAndColours: IRegionalEcosystemInfoAndColour[] = [
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatAorBAreaEndangered, colourDisplay: RegionalEcosystemColourEnum.reCatAorBAreaEndangered,
    reVmStatusString: ReVMStatusCodesEnum.reCatAorBAreaEndangered, reVmPoly: "E-dom",
    reVmStatLookupIds: [vmStatIdLookup.reCatAorBAreaEndangered_1, vmStatIdLookup.reCatAorBAreaEndangered_2]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatAorBAreaContainConcern, colourDisplay: RegionalEcosystemColourEnum.reCatAorBAreaContainConcern,
    reVmStatusString: ReVMStatusCodesEnum.reCatAorBAreaContainConcern, reVmPoly: "O-dom",
    reVmStatLookupIds: [vmStatIdLookup.reCatAorBAreaContainConcern_3, vmStatIdLookup.reCatAorBAreaContainConcern_4]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatAorBAreaLeastConcern, colourDisplay: RegionalEcosystemColourEnum.reCatAorBAreaLeastConcern,
    reVmStatusString: ReVMStatusCodesEnum.reCatAorBAreaLeastConcern, reVmPoly: "LeastC",
    reVmStatLookupIds: [vmStatIdLookup.reCatAorBAreaLeastConcern]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatCorRAreaEndangered, colourDisplay: RegionalEcosystemColourEnum.reCatCorRAreaEndangered,
    reVmStatusString: ReVMStatusCodesEnum.reCatCorRAreaEndangered, reVmPoly: "E-dom",
    reVmStatLookupIds: [vmStatIdLookup.reCatCorRAreaEndangered_11, vmStatIdLookup.reCatCorRAreaEndangered_12]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatCorRAreaContainConcern, colourDisplay: RegionalEcosystemColourEnum.reCatCorRAreaContainConcern,
    reVmStatusString: ReVMStatusCodesEnum.reCatCorRAreaContainConcern, reVmPoly: "O-dom",
    reVmStatLookupIds: [vmStatIdLookup.reCatCorRAreaContainConcern_13, vmStatIdLookup.reCatCorRAreaContainConcern_14]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reCatCorRAreaLeastConcern, colourDisplay: RegionalEcosystemColourEnum.reCatCorRAreaLeastConcern,
    reVmStatusString: ReVMStatusCodesEnum.reCatCorRAreaLeastConcern, reVmPoly: "LeastC",
    reVmStatLookupIds: [vmStatIdLookup.reCatCorRAreaLeastConcern]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reRemnant, colourDisplay: RegionalEcosystemColourEnum.reRemnant,
    reVmStatusString: ReVMStatusCodesEnum.reRemnant, reVmPoly: "rem_remnant",
    reVmStatLookupIds: [vmStatIdLookup.reUnknown]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reNonRemnant, colourDisplay: RegionalEcosystemColourEnum.reNonRemnant,
    reVmStatusString: ReVMStatusCodesEnum.reNonRemnant, reVmPoly: "non-rem",
    reVmStatLookupIds: [vmStatIdLookup.reUnknown]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reWater, colourDisplay: RegionalEcosystemColourEnum.reWater,
    reVmStatusString: ReVMStatusCodesEnum.reWater, reVmPoly: "water",
    reVmStatLookupIds: [vmStatIdLookup.reWater_9, vmStatIdLookup.reWater_19]
  },
  {
    reRuleIdDescription: RegionalEcosystemDescEnum.reRegrowthVegetation, colourDisplay: RegionalEcosystemColourEnum.reRegrowthVegetation,
    reVmStatusString: ReVMStatusCodesEnum.reRegrowthVegetation, reVmPoly: "hvr_regrowth",
    reVmStatLookupIds: [vmStatIdLookup.reUnknown]
  },
];


export interface AdminConfigModel {
  adminConfigId: number;
  purpose: string;
  configKey: string;
  title?: string;
  content: string;
  isShownAsActive: boolean;
  displayFrom?: string;
  displayUntil?: string;
  createdByUser: string;
  createdDateTime: string;
  updatedByUser?: string;
  updatedDateTime?: string;
  updateSeq: number;
  source?: string;
  isDeleted: boolean;
}
